export type SaveSubjectTypeDefinitionBody = {
	readonly wording: {
		readonly singular: string;
		readonly plural: string;
		readonly createAction: string;
		readonly emptyList: string;
		readonly sheetTitle: string | null;
		readonly aboutLabel: string;
		readonly titleLabel: string;
	};
	readonly icon: string;
	readonly color: string;
	readonly informationMode: string;
	readonly membersCanShare: boolean;
	readonly about: readonly string[];
	readonly fields: readonly {
		readonly key: string | null;
		readonly label: string;
		readonly nature: string;
		readonly options: readonly { readonly value: string | null; readonly label: string }[];
		readonly required: boolean;
		readonly sentToAi: boolean;
		readonly archived: boolean;
	}[];
	readonly speakerRoles: readonly { readonly key: string | null; readonly label: string }[];
	readonly usableBy: readonly {
		readonly kind: 'role' | 'user';
		readonly id: number;
		readonly level: string;
	}[];
	readonly workflowKeys: readonly string[];
};

const DEFAULT_ABOUT_LABEL = 'Porte sur';

/** `id === null` creates (`POST`), otherwise replaces the type `id` (`PUT`). */
class SaveSubjectTypeDefinition {
	constructor(
		public readonly accountId: number,
		public readonly id: number | null,
		public readonly body: SaveSubjectTypeDefinitionBody,
	) {
		Object.freeze(this);
	}

	// Explicit field picking: drafts carry display-only data (`natureLabel`) that `forbidNonWhitelisted`
	// would reject. A blank sheet title is sent as `null`; array order is the displayed order. A blank
	// "about" label of a type that is about nobody (its field is hidden) is sent as the default label.
	toJSON(): Record<string, unknown> {
		const { wording, fields, speakerRoles, usableBy } = this.body;
		const sheetTitle = wording.sheetTitle?.trim() ?? '';
		const aboutLabel = wording.aboutLabel.trim();

		return {
			wording: {
				singular: wording.singular,
				plural: wording.plural,
				createAction: wording.createAction,
				emptyList: wording.emptyList,
				sheetTitle: sheetTitle.length > 0 ? sheetTitle : null,
				aboutLabel:
					aboutLabel.length === 0 && this.body.about.length === 0
						? DEFAULT_ABOUT_LABEL
						: aboutLabel,
				titleLabel: wording.titleLabel.trim(),
			},
			icon: this.body.icon,
			color: this.body.color,
			informationMode: this.body.informationMode,
			membersCanShare: this.body.membersCanShare,
			about: [...this.body.about],
			fields: fields.map((field) => ({
				key: field.key,
				label: field.label,
				nature: field.nature,
				options: field.options.map((option) => ({ value: option.value, label: option.label })),
				required: field.required,
				sentToAi: field.sentToAi,
				archived: field.archived,
			})),
			speakerRoles: speakerRoles.map((role) => ({ key: role.key, label: role.label })),
			usableBy: usableBy.map((grant) => ({ kind: grant.kind, id: grant.id, level: grant.level })),
			workflowKeys: [...this.body.workflowKeys],
		};
	}
}

export default SaveSubjectTypeDefinition;
